import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import {
  RevenueResponseSchema,
  type RevenueResponse,
  type RevenueDateRange,
  getDefaultDateRange,
} from './types';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const REVENUE_DETAIL_KEY = 'revenue-detail';

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export interface UseRevenueReturn {
  data: RevenueResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  dateRange: RevenueDateRange;
  setDateRange: (range: RevenueDateRange) => void;
  refresh: () => void;
}

// ---------------------------------------------------------------------------
// Build query params from date range
// ---------------------------------------------------------------------------

function buildParams(dateRange: RevenueDateRange): Record<string, string> {
  return {
    from: dateRange.from,
    to: dateRange.to,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useRevenue(): UseRevenueReturn {
  const queryClient = useQueryClient();
  const [dateRange, setDateRange] = useState<RevenueDateRange>(getDefaultDateRange);

  const params = buildParams(dateRange);

  const query = useQuery<RevenueResponse>({
    queryKey: [REVENUE_DETAIL_KEY, params],
    queryFn: async () => {
      const raw = await api.get<RevenueResponse>('/api/revenue', { params });
      return RevenueResponseSchema.parse(raw);
    },
  });

  // The Refresh button on the Revenue page invalidates the revenue
  // breakdown query so a single click re-pulls the page's data.
  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [REVENUE_DETAIL_KEY] });
    void queryClient.invalidateQueries({ queryKey: ['revenue-transactions'] });
  }, [queryClient]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    dateRange,
    setDateRange,
    refresh,
  };
}
